import { useTranslation } from "react-i18next";
import { Icon, type IconKey } from "@core/assets";
import {
  PASSWORD_RULE_CODES,
  checkPasswordRules,
  cn,
  type PasswordRuleCode,
} from "@core/utils";

interface PasswordRulesProps {
  /** What the person has typed so far; the ticks follow it live. */
  password: string;
  /**
   * Rule codes the SERVER rejected (`password_policy_failed`). They are drawn as failed until the
   * password changes; the server stays the judge, the live ticks only anticipate it.
   */
  serverFailed?: readonly PasswordRuleCode[];
  className?: string;
}

const RULE_LABEL_KEYS = {
  min_length: "password.rules.min-length",
  digit: "password.rules.digit",
  uppercase: "password.rules.uppercase",
  symbol: "password.rules.symbol",
} as const satisfies Record<PasswordRuleCode, string>;

type RuleState = "unmet" | "met" | "failed";

const STATE_ICON: Record<RuleState, IconKey> = {
  unmet: "circle-dashed",
  met: "circle-check",
  failed: "circle-alert",
};

const STATE_STYLE: Record<RuleState, string> = {
  unmet: "text-(--text-tertiary)",
  met: "text-(--text-secondary)",
  failed: "text-(--text-error)",
};

const STATE_ICON_STYLE: Record<RuleState, string> = {
  unmet: "text-(--text-placeholder)",
  met: "text-(--feedback-success-icon)",
  failed: "text-(--feedback-error-icon)",
};

/**
 * The four password rules as a checklist (Figma: small text, a check circle per line, green
 * once met). The state is also spoken: each line ends with a visually hidden « respectée » /
 * « à respecter », so a screen reader gets what the colour shows.
 */
export function PasswordRules({
  password,
  serverFailed = [],
  className,
}: PasswordRulesProps) {
  const { t } = useTranslation("common");
  const met = checkPasswordRules(password);

  return (
    <ul
      aria-label={t("password.rules.label")}
      className={cn("flex flex-col gap-xxs", className)}
    >
      {PASSWORD_RULE_CODES.map((code) => {
        const state: RuleState = serverFailed.includes(code)
          ? "failed"
          : met[code]
            ? "met"
            : "unmet";
        return (
          <li
            key={code}
            className={cn(
              "flex items-center gap-xs text-xs-regular",
              STATE_STYLE[state]
            )}
          >
            <Icon
              name={STATE_ICON[state]}
              size={16}
              className={cn("shrink-0", STATE_ICON_STYLE[state])}
            />
            <span>{t(RULE_LABEL_KEYS[code])}</span>
            <span className="sr-only">
              {t(
                state === "met"
                  ? "password.rules.met"
                  : state === "failed"
                    ? "password.rules.failed"
                    : "password.rules.unmet"
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export type { PasswordRulesProps };
